import React from 'react';
import PropTypes from 'prop-types';
import RequestHandler from '../libs/requestHandling';
import EventDetailPage from '../event-details/EventDetail/EventDetailPage';
import {
  processCustomDataForEventWidget,
  setContactCustomData
} from '../libs/customDataFormatter';
import '../../polyfills/polyfill-allSettled';
import {
  filterTabsWithoutContent,
  filterVisibleEventInspectorTabs,
  getFullEventWidgetTabName
} from '../libs/tabFormatter';
import { withRouter } from '../libs/widgetUtilities';
import CacheResultsContext from '../components/CacheResultsContext';
import LoadingState from '../styled components/LoadingState';
import ContainerWrapper from '../styled components/ContainerWrapper';
import EventDetailError from '../components/Errors/EventDetailError';
import {
  formatAttachment,
  formatContactBooking,
  formatEventOrEventGroup,
  formatEventSchedules,
  formatGeneratedReport,
  formatResourceBooking
} from '../libs/ypAPIDataFormatter';
import { cacheResultForEventsKey } from '../libs/cacheKeys';
import FormatSettingsContext from '../components/FormatSettingsContext';
import { dateFormatOptions } from '../libs/dateFormatting';
import NavigationHistory from '../libs/NavigationHistory';

class EventContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      allContentFetched: false,
      event: {},
      contentPerTab: {},
      visibletabs: filterVisibleEventInspectorTabs(this.props.visibletabs.eventtabs),
      defaultTab: this.props.user.preferences.defaultEventInspectorTabApp,
      contactReferences: [],
      contactLinkReferences: [],
      eventTree: [],
      eventTreeReports: [],
      errorCode: false
    };

    this.fetchEventDetails();

    this.fetchAllGeneratedReportsInEventHierarchy =
      this.fetchAllGeneratedReportsInEventHierarchy.bind(this);
  }

  componentDidMount() {
    const { eventsFilterOptions, fetchEventFilterOptions } = this.props;
    if (eventsFilterOptions === null) fetchEventFilterOptions(() => {});
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const {
      widgetPageHeaderRef,
      sessionStorageUpdateCounter,
      cachingResultsUpdateCounter
    } = this.props;
    const {
      allContentFetched,
      contactLinkReferences,
      contactReferences,
      eventTree,
      eventTreeReports,
      errorCode
    } = this.state;
    return (
      widgetPageHeaderRef !== nextProps.widgetPageHeaderRef ||
      allContentFetched !== nextState.allContentFetched ||
      errorCode !== nextState.errorCode ||
      contactLinkReferences.length !== nextState.contactLinkReferences.length ||
      contactReferences.length !== nextState.contactReferences.length ||
      eventTree.length !== nextState.eventTree.length ||
      eventTreeReports.length !== nextState.eventTreeReports.length ||
      /* A value in the session storage has changed */
      sessionStorageUpdateCounter !== nextProps.sessionStorageUpdateCounter ||
      /* A value in the cache has changed */
      cachingResultsUpdateCounter !== nextProps.cachingResultsUpdateCounter
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { errorCode } = this.state;
    const { getCacheResultValue, resetCacheResultValue, cachingResultsUpdateCounter } =
      this.props;
    if (
      prevState.errorCode !== errorCode ||
      cachingResultsUpdateCounter !== prevProps.cachingResultsUpdateCounter
    ) {
      if (errorCode === 404) {
        const eventsCacheKey = cacheResultForEventsKey;
        const cachedResults = getCacheResultValue(eventsCacheKey);
        if (cachedResults) {
          resetCacheResultValue(eventsCacheKey);
        }
      }
    }
  }

  componentWillUnmount() {
    this.props.setCustomRendererForHeaderTitle(false);
  }

  fetchContactBookingsDetails(contactBookingsFromCustomData) {
    const contactbookings =
      this.state.contentPerTab[getFullEventWidgetTabName('Contacts')];
    const contactIds = new Set();
    const contactlinkIds = new Set();
    [...contactbookings, ...contactBookingsFromCustomData].forEach((booking) => {
      if (booking.isForContactLink()) contactlinkIds.add(booking.contactReferenceID);
      else contactIds.add(booking.contactReferenceID);
    });

    if (contactIds.size > 0)
      new RequestHandler().get(
        '/contacts',
        {
          ids: Array.from(contactIds),
          yesplanurl: this.props.user.host
        },
        (res, error) => {
          if (!error) {
            setContactCustomData(this.state.customDataPerTab, res.body, []);
            contactbookings
              .filter((booking) => !booking.isForContactLink())
              .forEach((booking) =>
                booking.setContactReference({ contacts: res.body, contactLinks: [] })
              );
            this.setState({
              contactReferences: res.body
            });
          }
        }
      );
    if (contactlinkIds.size > 0)
      new RequestHandler().get(
        '/contactlinks',
        {
          ids: Array.from(contactlinkIds),
          yesplanurl: this.props.user.host
        },
        (res, error) => {
          if (!error) {
            setContactCustomData(this.state.customDataPerTab, [], res.body);
            contactbookings
              .filter((booking) => booking.isForContactLink())
              .forEach((booking) =>
                booking.setContactReference({ contacts: [], contactLinks: res.body })
              );
            this.setState({
              contactLinkReferences: res.body
            });
          }
        }
      );
  }

  filterResourceBookingsOnType(bookings, type, searchInSets = false) {
    return bookings.filter((booking) => {
      if (searchInSets) return booking.isOfTypeorHasChildOfType(type);
      return booking.isOfType(type);
    });
  }

  makeRequestForReports(eventIdsForRequest, callback) {
    const { user } = this.props;
    new RequestHandler().get(
      '/groupreports',
      {
        ids: eventIdsForRequest,
        yesplanurl: user.host
      },
      (res, error) => {
        if (!error) {
          const { contentPerTab } = this.state;
          const reportsContent = contentPerTab[getFullEventWidgetTabName('Reports')];
          res.body.forEach((reportsForEvent, index) => {
            reportsContent[eventIdsForRequest[index]] = reportsForEvent.map(
              (reportJSON) => formatGeneratedReport(reportJSON)
            );
          });
          callback(contentPerTab);
        }
      }
    );
  }

  fetchGeneratedReportsInEventHierarchy() {
    const { user } = this.props;
    const { event } = this.state;
    if (event.isPartOfGroup())
      new RequestHandler().get(
        '/group',
        {
          id: event.topGroupID || event.directGroupID,
          yesplanurl: user.host
        },
        (res, error) => {
          if (!error) {
            const group = res.body;
            const constructPath = (node) => {
              if (!node.children) {
                if (node.id === event.id) return node;
                return false;
              }
              const rec = node.children.flatMap(constructPath);
              if (rec.every((n) => !n)) return [];
              return [node, ...rec.filter((n) => n)];
            };
            const eventTree = constructPath(group);
            if (eventTree) {
              /* Initially we only request a "summarized" view of reports in the event's path:
                 with a max. of 4, we fetch the reports for the event's direct parent group,
                 topmost group and 2 more groups in between.
                 eventTree contains the leaf event as well. */
              const eventTreeReports =
                eventTree.length > 5 ? [eventTree[0], ...eventTree.slice(-4)] : eventTree;
              const eventIdsForRequest = eventTreeReports
                .map((event) => event.id)
                .slice(0, -1);
              this.makeRequestForReports(eventIdsForRequest, (contentPerTab) => {
                this.setState({
                  eventTree,
                  eventTreeReports,
                  contentPerTab
                });
              });
            }
          }
        }
      );
  }

  fetchAllGeneratedReportsInEventHierarchy() {
    const { eventTree } = this.state;
    const fetchReportsEvents = eventTree.slice(1, -4);
    this.makeRequestForReports(
      fetchReportsEvents.map((event) => event.id),
      (contentPerTab) => {
        this.setState({
          contentPerTab,
          eventTreeReports: eventTree
        });
      }
    );
  }

  fetchEventDetails() {
    const { dateFormatSetting, timeFormatSetting } = this.props;
    const { id } = this.props.router.params;
    new RequestHandler().get(
      '/event',
      {
        id,
        yesplanurl: this.props.user.host,
        subpaths: [
          'customdata',
          'schedule',
          'contactbookings',
          'resourcebookings',
          'attachments',
          'reports'
        ]
      },
      (res, error) => {
        if (error) {
          /* In order to trigger the surrounding ErrorBoundary, we need to do a setState
              and throw the error in its callback. */
          this.setState({
            errorCode: error.code
          });
        } else if (res.statusCode !== 440) {
          const {
            event,
            customdata,
            definitions,
            schedule,
            contactbookings,
            resourcebookings,
            attachments,
            reports,
            ...otherDetails
          } = res.body;
          const eventObject = formatEventOrEventGroup(event);
          const { customDataPerTab, contactBookingObjects } =
            processCustomDataForEventWidget(definitions, customdata, {
              dateFormatSetting,
              timeFormatSetting
            });
          const contentPerTab = {};
          const allResourcesAsObjects = resourcebookings.map((bookingJSON) =>
            formatResourceBooking(bookingJSON)
          );
          contentPerTab[getFullEventWidgetTabName('Schedules')] =
            formatEventSchedules(schedule);
          contentPerTab[getFullEventWidgetTabName('Contacts')] = contactbookings.map(
            (bookingJSON) => formatContactBooking(bookingJSON)
          );
          contentPerTab[getFullEventWidgetTabName('Resources')] = allResourcesAsObjects;
          contentPerTab[getFullEventWidgetTabName('Attachments')] = attachments.map(
            (attachmentJSON) => formatAttachment(attachmentJSON)
          );
          contentPerTab[getFullEventWidgetTabName('Reports')] = {
            [id]: reports.map((reportJSON) => formatGeneratedReport(reportJSON))
          };
          contentPerTab[getFullEventWidgetTabName('Location')] =
            this.filterResourceBookingsOnType(allResourcesAsObjects, 'Location');
          contentPerTab[getFullEventWidgetTabName('Personnel')] =
            this.filterResourceBookingsOnType(allResourcesAsObjects, 'Human', true);

          const visibletabs = filterTabsWithoutContent(
            this.state.visibletabs,
            contentPerTab,
            customDataPerTab
          );
          this.setState(
            {
              allContentFetched: true,
              ...otherDetails,
              event: eventObject,
              customDataPerTab,
              contentPerTab,
              visibletabs,
              errorCode: false
            },
            () => {
              this.fetchContactBookingsDetails(contactBookingObjects);
              this.fetchGeneratedReportsInEventHierarchy();
            }
          );
        }
      }
    );
  }

  renderErrorMessage() {
    const { widgetPageHeaderRef, navigationHistory } = this.props;
    const { errorCode } = this.state;

    return (
      <ContainerWrapper
        $paddingTop={
          widgetPageHeaderRef.current ? widgetPageHeaderRef.current.getHeaderHeight() : 0
        }
        data-test-type="errorMessage"
      >
        <EventDetailError errorCode={errorCode} navigationHistory={navigationHistory} />
      </ContainerWrapper>
    );
  }

  render() {
    const {
      previousURL,
      widgetPageHeaderRef,
      setCustomRendererForHeaderTitle,
      user,
      navigationHistory,
      statuses,
      profiles,
      widgetAnimationWrapperRef
    } = this.props;
    const { errorCode, allContentFetched } = this.state;
    if (errorCode) return this.renderErrorMessage();
    if (!allContentFetched) return <LoadingState $show={!allContentFetched} />;
    return (
      <EventDetailPage
        {...this.state}
        statuses={statuses}
        profiles={profiles}
        previousURL={previousURL}
        widgetPageHeaderRef={widgetPageHeaderRef}
        widgetAnimationWrapperRef={widgetAnimationWrapperRef}
        setCustomRendererForHeaderTitle={setCustomRendererForHeaderTitle}
        user={user}
        navigationHistory={navigationHistory}
        fetchAllGeneratedReportsInEventHierarchy={
          this.fetchAllGeneratedReportsInEventHierarchy
        }
      />
    );
  }
}

EventContainer.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    preferences: PropTypes.shape({
      defaultEventInspectorTabApp: PropTypes.shape({})
    })
  }).isRequired,
  match: PropTypes.shape({
    path: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    url: PropTypes.string,
    isExact: PropTypes.bool,
    params: PropTypes.shape({
      id: PropTypes.string,
      datePattern: PropTypes.string
    })
  }).isRequired,
  visibletabs: PropTypes.shape({
    eventtabs: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  setCustomRendererForHeaderTitle: PropTypes.func.isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  previousURL: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  widgetPageHeaderRef: PropTypes.shape({}).isRequired,
  eventsFilterOptions: PropTypes.shape({}),
  sessionStorageUpdateCounter: PropTypes.number.isRequired,
  fetchEventFilterOptions: PropTypes.func.isRequired,
  resetCacheResultValue: PropTypes.func.isRequired,
  getCacheResultValue: PropTypes.func.isRequired,
  setCacheResultValue: PropTypes.func.isRequired,
  cachingResultsUpdateCounter: PropTypes.number.isRequired,
  dateFormatSetting: PropTypes.oneOf(dateFormatOptions).isRequired,
  statuses: PropTypes.shape({}),
  profiles: PropTypes.shape({}),
  widgetAnimationWrapperRef: PropTypes.shape({
    current: PropTypes.oneOfType([() => null, PropTypes.shape({})])
  }).isRequired
};

EventContainer.defaultProps = {
  eventsFilterOptions: null,
  statuses: {},
  profiles: {}
};

function ForwardRef(props, ref) {
  return (
    <FormatSettingsContext.Consumer>
      {(contextProps) => (
        <CacheResultsContext.Consumer>
          {(cacheResultsContextProps) => (
            <EventContainer {...props} {...cacheResultsContextProps} {...contextProps} />
          )}
        </CacheResultsContext.Consumer>
      )}
    </FormatSettingsContext.Consumer>
  );
}

export default withRouter(React.forwardRef(ForwardRef));
